import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../config/supabase';
import { Calendar, Clock, CurrencyCircleDollar, FileText, Warning } from '@phosphor-icons/react';
import styles from './PublicQuoteView.module.css';

/**
 * PublicQuoteView - Public page for vendors to view shared quotes
 *
 * Accessible via /quote/view/:token without authentication.
 * Shows quote details, line items, and attachments.
 * No project data leakage - only shows the specific quote.
 */
const PublicQuoteView = () => {
  const { token } = useParams();

  // Fetch quote by share token (no auth required due to RLS policy)
  const {
    data: quote,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['public-quote', token],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_quotes')
        .select(
          `
          id,
          total_price,
          validity_date,
          lead_time_days,
          payment_terms,
          notes,
          attachments,
          status,
          created_at,
          master_vendors (
            id,
            name,
            contact_person,
            contact_email,
            contact_phone
          )
        `,
        )
        .eq('share_token', token)
        .eq('share_enabled', true)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  // Fetch quote items
  const { data: quoteItems } = useQuery({
    queryKey: ['public-quote-items', quote?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_quote_items')
        .select('*')
        .eq('quote_id', quote.id);

      if (error) throw error;
      return data;
    },
    enabled: !!quote?.id,
  });

  // Check if quote is expired
  const isExpired = useMemo(() => {
    if (!quote?.validity_date) return false;
    return new Date(quote.validity_date) < new Date();
  }, [quote?.validity_date]);

  // Format date
  const formatDate = dateStr => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format price
  const formatPrice = amount => {
    if (!amount) return '-';
    return `₪${Number(amount).toLocaleString('he-IL')}`;
  };

  // Get status label
  const getStatusLabel = status => {
    const labels = {
      draft: 'טיוטה',
      sent_to_vendor: 'נשלח לספק',
      vendor_clarification: 'בירור עם ספק',
      quote_received: 'התקבלה הצעה',
      selected: 'נבחר',
      rejected: 'נדחה',
    };
    return labels[status] || status;
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>טוען הצעת מחיר...</span>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className={styles.container}>
        <div className={styles.errorCard}>
          <Warning size={48} className={styles.errorIcon} />
          <h1 className={styles.errorTitle}>הצעת המחיר לא נמצאה</h1>
          <p className={styles.errorDescription}>
            הקישור אינו תקף או שהשיתוף בוטל.
            <br />
            אנא פנה למנהל הפרויקט לקבלת קישור חדש.
          </p>
        </div>
      </div>
    );
  }

  const vendor = quote.master_vendors;

  return (
    <div className={styles.container} dir="rtl">
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.logo}>KAMUYOT</div>
        <div className={styles.headerBadge}>הצעת מחיר</div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Expired Warning */}
        {isExpired && (
          <div className={styles.expiredBanner}>
            <Warning size={20} />
            <span>תוקף הצעת המחיר פג ב-{formatDate(quote.validity_date)}</span>
          </div>
        )}

        {/* Quote Card */}
        <div className={styles.quoteCard}>
          {/* Vendor Info */}
          <div className={styles.vendorSection}>
            <h1 className={styles.vendorName}>{vendor?.name || 'ספק'}</h1>
            {vendor?.contact_person && (
              <p className={styles.vendorContact}>{vendor.contact_person}</p>
            )}
            <div className={styles.vendorDetails}>
              {vendor?.contact_email && (
                <a href={`mailto:${vendor.contact_email}`} className={styles.vendorLink}>
                  {vendor.contact_email}
                </a>
              )}
              {vendor?.contact_phone && (
                <a href={`tel:${vendor.contact_phone}`} className={styles.vendorLink}>
                  {vendor.contact_phone}
                </a>
              )}
            </div>
          </div>

          {/* Price Section */}
          <div className={styles.priceSection}>
            <div className={styles.priceLabel}>מחיר כולל</div>
            <div className={styles.priceValue}>{formatPrice(quote.total_price)}</div>
            <div className={`${styles.statusBadge} ${styles[`status_${quote.status}`]}`}>
              {getStatusLabel(quote.status)}
            </div>
          </div>

          {/* Details Grid */}
          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <Calendar size={20} className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <div className={styles.detailLabel}>תוקף ההצעה</div>
                <div className={`${styles.detailValue} ${isExpired ? styles.expired : ''}`}>
                  {formatDate(quote.validity_date)}
                </div>
              </div>
            </div>
            <div className={styles.detailItem}>
              <Clock size={20} className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <div className={styles.detailLabel}>זמן אספקה</div>
                <div className={styles.detailValue}>
                  {quote.lead_time_days ? `${quote.lead_time_days} ימים` : '-'}
                </div>
              </div>
            </div>
            <div className={styles.detailItem}>
              <CurrencyCircleDollar size={20} className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <div className={styles.detailLabel}>תנאי תשלום</div>
                <div className={styles.detailValue}>{quote.payment_terms || '-'}</div>
              </div>
            </div>
            <div className={styles.detailItem}>
              <FileText size={20} className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <div className={styles.detailLabel}>תאריך יצירה</div>
                <div className={styles.detailValue}>{formatDate(quote.created_at)}</div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {quote.notes && (
            <div className={styles.notesSection}>
              <h3 className={styles.sectionTitle}>הערות</h3>
              <p className={styles.notesContent}>{quote.notes}</p>
            </div>
          )}

          {/* Line Items */}
          {quoteItems && quoteItems.length > 0 && (
            <div className={styles.itemsSection}>
              <h3 className={styles.sectionTitle}>פריטים בהצעה</h3>
              <div className={styles.itemsList}>
                {quoteItems.map((item, index) => (
                  <div key={item.id} className={styles.lineItem}>
                    <div className={styles.lineItemNumber}>{index + 1}</div>
                    <div className={styles.lineItemContent}>
                      {item.line_notes && (
                        <div className={styles.lineItemNotes}>{item.line_notes}</div>
                      )}
                      <div className={styles.lineItemMeta}>
                        {item.project_item_ids?.length > 0 && (
                          <span>{item.project_item_ids.length} פריטים</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.lineItemPrice}>{formatPrice(item.line_price)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {quote.attachments && quote.attachments.length > 0 && (
            <div className={styles.attachmentsSection}>
              <h3 className={styles.sectionTitle}>קבצים מצורפים</h3>
              <div className={styles.attachmentsList}>
                {quote.attachments
                  .filter(att => !att.deleted_at)
                  .map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.attachmentLink}
                    >
                      <FileText size={18} />
                      <span>{attachment.name || `קובץ ${index + 1}`}</span>
                    </a>
                  ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <p>מופעל על ידי Kamuyot</p>
      </footer>
    </div>
  );
};

export default PublicQuoteView;
